import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { FaHandPointRight } from 'react-icons/fa';

interface Props {
    /** Seletor do elemento apontado. */
    seletor: string;
    /** Muda quando o passo muda: a tela rola até o novo alvo uma vez. */
    chave: string;
    titulo: string;
    texto: string;
    botao?: string;
    aoConfirmar?: () => void;
}

interface Retangulo { top: number; left: number; width: number; height: number; }
type Lado = 'acima' | 'abaixo' | 'esquerda' | 'direita';

const LARGURA = 250;
const ESPACO = 14;

const surgir = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

// Destaque discreto: borda fina no azul do sistema e um halo leve, sem piscar.
const Destaque = styled.div`
    position: fixed;
    z-index: 2000;
    border: 2px solid rgba(31, 70, 126, 0.7);
    border-radius: 8px;
    box-shadow: 0 0 0 4px rgba(31, 70, 126, 0.12);
    pointer-events: none;
    animation: ${surgir} 0.25s ease-out;
    transition: top 0.15s, left 0.15s, width 0.15s, height 0.15s;
`;

// Balão branco com borda no degradê azul do botão "Acesso Rápido": aparece tanto sobre o fundo
// azul do onboarding quanto sobre o formulário branco (o balão azul-escuro sumia no fundo azul).
const AZUL_INICIO = '#143C8F';
const AZUL_FIM = '#335EDB';
const AZUL_MEIO = '#2450B8';
const DEGRADE = `linear-gradient(90deg, ${AZUL_INICIO} 0%, ${AZUL_FIM} 100%)`;

const Caixa = styled.div<{ $lado: Lado; $atravessavel: boolean }>`
    position: fixed;
    z-index: 2001;
    width: ${LARGURA}px;
    max-width: calc(100vw - 16px);
    padding: 14px 16px;
    border-radius: 12px;
    color: #374151;
    border: 2px solid transparent;
    background: linear-gradient(#ffffff, #ffffff) padding-box, ${DEGRADE} border-box;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.35);
    font-size: 14px;
    line-height: 1.45;
    /* Em cima do formulário (tela estreita), deixa os cliques passarem para os campos; só o botão
       do balão continua clicável. */
    pointer-events: ${p => p.$atravessavel ? 'none' : 'auto'};

    /* Seta: quadrado branco girado, com a borda (no tom do degradê daquele lado) só nos dois lados de fora. */
    &::before {
        content: '';
        position: absolute;
        width: 14px;
        height: 14px;
        background: #ffffff;
        transform: rotate(45deg);
        ${p => p.$lado === 'acima' ? `bottom: -9px; left: var(--seta, 24px); border-right: 2px solid ${AZUL_MEIO}; border-bottom: 2px solid ${AZUL_MEIO};`
        : p.$lado === 'abaixo' ? `top: -9px; left: var(--seta, 24px); border-left: 2px solid ${AZUL_MEIO}; border-top: 2px solid ${AZUL_MEIO};`
        : p.$lado === 'direita' ? `left: -9px; top: var(--seta, 24px); border-left: 2px solid ${AZUL_INICIO}; border-bottom: 2px solid ${AZUL_INICIO};`
        : `right: -9px; top: var(--seta, 24px); border-right: 2px solid ${AZUL_FIM}; border-top: 2px solid ${AZUL_FIM};`}
    }
`;

const Rotulo = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${AZUL_FIM};
`;

const Titulo = styled.div`
    font-weight: 700;
    font-size: 15px;
    margin-bottom: 4px;
    color: #1F467E;
`;

const Botao = styled.button`
    margin-top: 10px;
    padding: 6px 16px;
    border: none;
    border-radius: 6px;
    background: ${DEGRADE};
    color: #ffffff;
    font-weight: 700;
    font-size: 13px;
    cursor: pointer;
    pointer-events: auto;

    &:hover { filter: brightness(1.08); }
`;

interface Medida { alvo: Retangulo; modal: Retangulo | null; }

const retangulo = (r: DOMRect): Retangulo => ({ top: r.top, left: r.left, width: r.width, height: r.height });

// Área em que o alvo aparece de fato: recortada pelas caixas com rolagem própria (o corpo do
// formulário) e pela janela. Sem isso, com o campo rolado para fora do corpo, o destaque e o balão
// seguiam o campo "escondido" e iam parar em cima do que está acima do formulário.
// O retângulo do campo inteiro: alguns campos (o checkbox) desenham fora da própria caixa, e o
// destaque passava por cima da caixinha em vez de contorná-la.
const retanguloDoCampo = (el: Element) => {
    const r = el.getBoundingClientRect();
    let topo = r.top, esquerda = r.left, base = r.bottom, direita = r.right;
    el.querySelectorAll('*').forEach(filho => {
        const f = filho.getBoundingClientRect();
        if (f.width === 0 || f.height === 0) return;
        topo = Math.min(topo, f.top);
        esquerda = Math.min(esquerda, f.left);
        base = Math.max(base, f.bottom);
        direita = Math.max(direita, f.right);
    });
    return { top: topo, left: esquerda, bottom: base, right: direita };
};

const parteVisivel = (el: Element): Retangulo | null => {
    const r = retanguloDoCampo(el);
    let topo = Math.max(r.top, 0);
    let base = Math.min(r.bottom, window.innerHeight);
    for (let pai = el.parentElement; pai; pai = pai.parentElement) {
        const estilo = window.getComputedStyle(pai);
        if (/(auto|scroll|hidden)/.test(estilo.overflowY) && pai.scrollHeight > pai.clientHeight) {
            const c = pai.getBoundingClientRect();
            topo = Math.max(topo, c.top);
            base = Math.min(base, c.bottom);
        }
    }
    if (base - topo < 12) return null;
    return { top: topo, left: r.left, width: r.right - r.left, height: base - topo };
};

const medir = (seletor: string): Medida | null => {
    const el = document.querySelector(seletor);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return null;
    const visivel = parteVisivel(el);
    if (!visivel) return null;
    const modal = el.closest('.modal-content, [data-guia-container]');
    return { alvo: visivel, modal: modal ? retangulo(modal.getBoundingClientRect()) : null };
};

const limitar = (valor: number, min: number, max: number) => Math.max(min, Math.min(valor, max));

/**
 * Onde o balão fica. Com espaço ao lado do modal, fica fora do formulário (não cobre campo
 * nenhum), na altura do alvo. Sem espaço (tela estreita), abaixo ou acima do alvo.
 */
const posicionar = ({ alvo, modal }: Medida, altura: number) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const centroY = alvo.top + alvo.height / 2;
    const topoAoLado = limitar(centroY - altura / 2, 8, vh - altura - 8);
    const setaAoLado = limitar(centroY - topoAoLado - 7, 12, altura - 26);

    if (modal && vw - (modal.left + modal.width) >= LARGURA + ESPACO + 4)
        return { lado: 'direita' as Lado, top: topoAoLado, left: modal.left + modal.width + ESPACO, seta: setaAoLado, atravessavel: false };
    if (modal && modal.left >= LARGURA + ESPACO + 4)
        return { lado: 'esquerda' as Lado, top: topoAoLado, left: modal.left - ESPACO - LARGURA, seta: setaAoLado, atravessavel: false };

    const cabeAbaixo = alvo.top + alvo.height + ESPACO + altura < vh - 8;
    const acima = !cabeAbaixo && alvo.top - ESPACO - altura > 8;
    const left = limitar(alvo.left, 8, vw - LARGURA - 8);
    return {
        lado: (acima ? 'acima' : 'abaixo') as Lado,
        top: acima ? alvo.top - ESPACO - altura : Math.min(alvo.top + alvo.height + ESPACO, vh - altura - 8),
        left,
        seta: limitar(alvo.left + Math.min(alvo.width, 80) / 2 - left - 7, 16, LARGURA - 30),
        atravessavel: true,
    };
};

/**
 * Balão da criação guiada. Não bloqueia nada: o usuário continua usando o formulário e o balão
 * acompanha o alvo (a posição é relida enquanto a tela rola ou muda de tamanho).
 */
export default function BalaoGuia({ seletor, chave, titulo, texto, botao, aoConfirmar }: Props) {
    const [medida, setMedida] = useState<Medida | null>(null);
    const caixa = useRef<HTMLDivElement>(null);
    const [alturaCaixa, setAlturaCaixa] = useState(120);

    useEffect(() => {
        let ativo = true;
        const atualizar = () => { if (ativo) setMedida(medir(seletor)); };
        atualizar();
        const intervalo = window.setInterval(atualizar, 200);
        window.addEventListener('resize', atualizar);
        window.addEventListener('scroll', atualizar, true);
        return () => {
            ativo = false;
            window.clearInterval(intervalo);
            window.removeEventListener('resize', atualizar);
            window.removeEventListener('scroll', atualizar, true);
        };
    }, [seletor]);

    // Leva o alvo para a vista quando o passo muda (o campo pode estar abaixo da dobra do modal).
    useEffect(() => {
        let tentativas = 0;
        const rolar = window.setInterval(() => {
            const el = document.querySelector(seletor);
            if (el || ++tentativas > 15) {
                window.clearInterval(rolar);
                el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }, 100);
        return () => window.clearInterval(rolar);
    }, [chave, seletor]);

    useLayoutEffect(() => {
        if (caixa.current) setAlturaCaixa(caixa.current.offsetHeight);
    }, [titulo, texto, botao, medida !== null]);

    if (!medida) return null;

    const { alvo } = medida;
    const { lado, top, left, seta, atravessavel } = posicionar(medida, alturaCaixa);

    return ReactDOM.createPortal(
        <>
            <Destaque style={{ top: alvo.top - 3, left: alvo.left - 3, width: alvo.width + 6, height: alvo.height + 6 }} />
            <Caixa ref={caixa} $lado={lado} $atravessavel={atravessavel} style={{ top, left, ['--seta' as any]: `${seta}px` }} role="status" aria-live="polite" data-guia-alvo={seletor}>
                <Rotulo><FaHandPointRight /> Passo a passo</Rotulo>
                <Titulo>{titulo}</Titulo>
                <div>{texto}</div>
                {botao && aoConfirmar && <Botao type="button" onClick={aoConfirmar}>{botao}</Botao>}
            </Caixa>
        </>,
        document.body
    );
}
